<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { __ } from '@@/bits/intl'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import type { FramePreview } from '@@/pinia/whiteboard_frame_preview'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { useForceChangeCursor } from '@@/vuecomposables/force_change_cursor'
import type { TLPageId } from 'tldraw'
import { computed, ref, watch } from 'vue'
// const emit = defineEmits(['switch-frame', 'show-context-menu', 'move-frame-above', 'move-frame-below'])
const emit = defineEmits<{
  (name: 'switch-frame', frameId: string): void
  (name: 'show-context-menu', event: MouseEvent, frameId: string): void
  (name: 'move-frame-above', payload: { frameId: TLPageId; targetFrameId: TLPageId }): void
  (name: 'move-frame-below', payload: { frameId: TLPageId; targetFrameId: TLPageId }): void
}>()

const props = withDefaults(
  defineProps<{
    frameId: string
    frameIndex: number
    currentFrameId: string | null
    colorScheme: 'light' | 'dark'
    isLoadingPortraitMobileLayout: boolean
    preview?: FramePreview
    isDraggable: boolean
    hideContextMenuButton?: boolean
  }>(),
  {
    frameId: '',
    frameIndex: 1,
    currentFrameId: '',
    colorScheme: 'light',
    isLoadingPortraitMobileLayout: false,
    preview: undefined,
    isDraggable: false,
    hideContextMenuButton: false,
  },
)

function switchFrame(frameId: string) {
  emit('switch-frame', frameId)
}

function showContextMenu(event: MouseEvent, frameId: string) {
  emit('show-context-menu', event, frameId)
}
const itemData = computed<{ frameId: TLPageId }>(() => ({ frameId: props.frameId as TLPageId }))

/** Drag and drop */
const elementRef = ref<HTMLElement | undefined>(undefined)
const buttonRef = ref<typeof OzBaseButton | undefined>(undefined)

const { forceChangeCursor, resetCursor } = useForceChangeCursor()
const { isDragging } = useDraggableElement({
  elementRef,
  type: 'whiteboard_framebar_preview',
  data: itemData,
  dragPreview: computed(() => buttonRef.value?.$el),
  dragPreviewComponentProps: ref({
    currentFrameId: null,
    xContextMenuButton: false,
  }),
  isDraggable: computed(() => props.isDraggable),
  onDragStart: () => {
    forceChangeCursor('grabbing')
  },
  onDrop: () => {
    trackEvent('Whiteboard', 'Moved frame position', null, null, { context: 'Drag and drop' })
    resetCursor()
  },
})

const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef,
  type: 'whiteboard_framebar_preview',
  data: itemData,
})
addDraggableSource<{ frameId: TLPageId }>({
  type: 'whiteboard_framebar_preview',
  axis: props.isLoadingPortraitMobileLayout ? 'horizontal' : 'vertical',
  axisRef: computed(() => (props.isLoadingPortraitMobileLayout ? 'horizontal' : 'vertical')),
  canDrop: () => {
    return true
  },
  onDragEnter: () => {},
  onDrop: (e) => {
    if (e.relativePositionToTarget === 'before') {
      emit('move-frame-above', { frameId: e.sourceItem.data.frameId, targetFrameId: e.targetItem.data.frameId })
    }
    if (e.relativePositionToTarget === 'after') {
      emit('move-frame-below', { frameId: e.sourceItem.data.frameId, targetFrameId: e.targetItem.data.frameId })
    }
  },
})

const oldPreviewPng = ref<string | undefined>(undefined)
const currentPreviewPng = ref<string | undefined>(props.preview?.type === 'png' ? props.preview.png : undefined)

// When the preview png changes, we transition the previewPng to the new value.
watch(
  () => props.preview,
  (newVal) => {
    if (newVal?.type === 'png') {
      oldPreviewPng.value = currentPreviewPng.value
      currentPreviewPng.value = newVal.png
    } else {
      oldPreviewPng.value = undefined
      currentPreviewPng.value = undefined
    }
  },
)

async function handlePreviewImageError() {
  if (props.preview?.type !== 'png') {
    return
  }
  const response = await fetch(props.preview.png, {
    method: 'POST',
    body: props.preview.svgTag,
  })
  if (response.ok) {
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    currentPreviewPng.value = url
    oldPreviewPng.value = undefined
  }
}
</script>

<template>
  <div
    ref="elementRef"
    :class="['frame-preview-container', 'relative', isLoadingPortraitMobileLayout && 'mb-9']"
    :data-testid="`whiteboardFrame${frameIndex}Preview`"
  >
    <OzBaseButton
      ref="buttonRef"
      :data-testid="`whiteboardFrame${frameIndex}PreviewButton`"
      :class="[
        'p-0',
        'overflow-hidden',

        {
          'w-[69px] h-10': !isLoadingPortraitMobileLayout, // 72px - 3px for ring (not included in border-box)
          'w-[96px] h-13.5': isLoadingPortraitMobileLayout,
        },
        'box-border',
        'shrink-0',
        'group',
        isDragging && {
          'animate-pulse-bg-color pulse-bg-from-white': true,
          'pulse-bg-to-light-ui-300': colorScheme === 'light',
          'pulse-bg-to-grey-200': colorScheme === 'dark',
        },
        'text-dark-text-100',
        'rounded-md',
        'focus-visible:ring-2 focus-visible:ring-offset-2',
        {
          'bg-light-ui-100': currentFrameId === frameId,
          'bg-light-ui-200': currentFrameId !== frameId,
          'border-1 !border-solid border-[var(--whiteboard-frame-preview-border-light)]':
            currentFrameId !== frameId && colorScheme === 'light',
          'hhover:border-0 hactive:border-0': currentFrameId !== frameId && colorScheme === 'light',
          'focus-visible:ring-offset-light-ui-100 focus-visible:ring-grape-500': colorScheme === 'light',
          'focus-visible:ring-offset-dark-ui-100  focus-visible:ring-canary-500': colorScheme === 'dark',
        },
        'transform-gpu',
        'duration-150',
      ]"
      @click="switchFrame(frameId)"
      @contextmenu.prevent="showContextMenu($event, frameId)"
    >
      <!-- Top-left corner number -->
      <div
        :data-testid="
          currentFrameId === frameId
            ? `currentWhiteboardFramePreview${frameIndex}Index`
            : `whiteboardFramePreview${frameIndex}Index`
        "
        :class="[
          'absolute',
          'top-0',
          'start-0',
          'duration-100',

          'text-11-16',
          'font-semibold',
          'rounded-ee-lg',
          'z-10',
          'flex justify-center items-center',
          {
            'bg-dark-ui-200 text-light-text-200 hover-hover:group-hover:text-light-text-100':
              currentFrameId !== frameId && colorScheme === 'dark',
            'bg-canary-500 text-dark-text-100': currentFrameId === frameId && colorScheme === 'dark',
            'bg-[var(--whiteboard-frame-preview-border-light)] text-dark-text-200 hover-hover:group-hover:text-dark-text-100':
              currentFrameId !== frameId && colorScheme === 'light',
            'bg-grape-500 text-light-text-100': currentFrameId === frameId && colorScheme === 'light',
          },
        ]"
      >
        <div
          :class="[
            'transition-[padding]',
            'h-4',
            'w-4',
            (currentFrameId === frameId || colorScheme === 'dark') && 'ps-0.25',
            'group-hover:ps-0.25 group-hover:pt-0.25',
            'pe-0.5',
            (currentFrameId === frameId || colorScheme === 'dark') && 'pt-0.25',
            'pb-0.5',
          ]"
        >
          {{ frameIndex }}
        </div>
      </div>

      <template v-if="preview">
        <div
          v-if="preview.type === 'svg'"
          :data-testid="`whiteboardFrame${frameIndex}PreviewHtml`"
          :class="[
            'bg-contain w-full h-full transition-all duration-300 ease-in-out frame-preview pointer-events-none',
            {
              'opacity-80 hover-hover:group-hover:opacity-100': currentFrameId !== frameId && colorScheme === 'dark',
            },
          ]"
          v-html="preview.svgTag"
        />
        <div
          v-else-if="preview.type === 'png'"
          :data-testid="`whiteboardFrame${frameIndex}PreviewHtmlV2`"
          :class="[
            'bg-contain w-full h-full transition-all duration-300 ease-in-out frame-preview pointer-events-none',
            {
              'opacity-80 hover-hover:group-hover:opacity-100': currentFrameId !== frameId && colorScheme === 'dark',
            },
          ]"
        >
          <div class="absolute inset-0 w-full h-full" v-html="preview.backgroundTag" />

          <!-- N.B.: We transition the previewPng to the new value so that we can avoid a flash when the preview png changes. -->
          <img v-if="oldPreviewPng" class="absolute inset-0 w-full h-full" :src="oldPreviewPng" alt="" />
          <img
            v-if="currentPreviewPng"
            :class="[
              'absolute inset-0 w-full h-full text-[0pt]',
              {
                hidden: oldPreviewPng != null,
              },
            ]"
            :src="currentPreviewPng"
            :alt="__('Preview for card %{cardNumber}', { cardNumber: frameIndex + 1 })"
            @error="handlePreviewImageError"
          />
        </div>
      </template>
      <div
        v-if="currentFrameId !== frameId && colorScheme === 'light'"
        class="absolute inset-0 bg-grey-1000 group-hover:hidden group-active:hidden opacity-[3%]"
      />
    </OzBaseButton>
    <!-- Context menu button -->
    <OzPlainButton
      v-if="!hideContextMenuButton"
      :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
      :size-preset="OzPlainButtonSizePreset.Bare"
      :dark-mode="colorScheme === 'dark'"
      :class="[
        'absolute',
        {
          'top-0 end-[-18px]': !isLoadingPortraitMobileLayout,
          '-bottom-9 start-1/2 -translate-s-1/2 h-6 w-6': isLoadingPortraitMobileLayout,
        },

        // Increase click area
        'p-0 pb-1',
        // 'py-2 pe-1 ps-0',

        // Focus
        'focus-visible:ring-3',
        'rounded-sm focus-visible:pb-0',
        colorScheme === 'light' && 'focus-visible:ring-grape-500',
        colorScheme === 'dark' && 'focus-visible:ring-canary-500',
      ]"
      :aria-label="__('More card actions')"
      :title="__('More card actions')"
      :data-testid="`whiteboardFrame${frameIndex}PreviewOpenContextMenuButton`"
      @click="showContextMenu($event, frameId)"
    >
      <OzIcon
        name="more_vertical_thin"
        :class="{ 'rotate-90': isLoadingPortraitMobileLayout }"
        :size="isLoadingPortraitMobileLayout ? 24 : 16"
      />
    </OzPlainButton>
    <OzDropIndicatorLine
      v-if="closestEdgeToPointer"
      :edge="closestEdgeToPointer"
      :gap="isLoadingPortraitMobileLayout ? 6 : 12"
      :dark-mode="colorScheme === 'dark'"
    />
  </div>
</template>
<style scoped lang="postcss">
.frame-preview-container {
  --whiteboard-frame-preview-border-light: #dedede;
}

.frame-preview /deep/ svg,
.frame-preview /deep/ div {
  width: 100%;
  height: 100%;
}
</style>
